import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  FitnessCenter as FitnessIcon,
  PlayArrow as PlayIcon,
  TrendingUp as DifficultyIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface MasterFitness {
  id: string;
  fitnessCode: string;
  fitnessName: string;
  fitnessType: string;
  metricName?: string;
  metricUnit?: string;
  description?: string;
  instructions?: string;
  referenceVideos?: string[];
  difficultyLevel?: number;
}

const PlayerFitnessPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sport = searchParams.get('sport') || 'Badminton';
  const level = searchParams.get('level') || 'beginner';

  const [fitness, setFitness] = useState<MasterFitness[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFitness, setSelectedFitness] = useState<MasterFitness | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string>(level);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  const loadFitness = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-fitness?sport=${sport}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        // Filter by level if not 'all' (for now, show all - level filtering will be based on mappings later)
        let filteredFitness = data.data || [];
        
        // TODO: Filter by skill level based on player_skill_levels and skill_level_fitness_mapping
        // For MVP, show all active fitness
        filteredFitness = filteredFitness.filter((f: MasterFitness) => true); // All fitness for now
        
        setFitness(filteredFitness);
      } else {
        setError(data.message || 'Failed to load fitness');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load fitness');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFitness();
  }, [sport, selectedLevel]);

  const handleViewDetail = (fitnessItem: MasterFitness) => {
    setSelectedFitness(fitnessItem);
    setDetailDialogOpen(true);
  };

  const handleLevelChange = (newLevel: string) => {
    setSelectedLevel(newLevel);
    navigate(`/player/fitness?sport=${sport}&level=${newLevel}`);
  };

  const getDifficultyColor = (level?: number) => {
    if (!level) return 'default';
    if (level >= 4) return 'error';
    if (level >= 3) return 'warning';
    return 'success';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Fitness Benchmarks - {sport}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Track your fitness and physical performance
        </Typography>
      </Box>

      {/* Level Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Skill Level</InputLabel>
          <Select
            value={selectedLevel}
            label="Skill Level"
            onChange={(e) => handleLevelChange(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="beginner">Beginner</MenuItem>
            <MenuItem value="intermediate">Intermediate</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : fitness.length === 0 ? (
        <Alert severity="info">
          No fitness benchmarks available for {sport} at {selectedLevel === 'all' ? 'any' : selectedLevel} level.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {fitness.map((fitnessItem) => (
            <Grid item xs={12} sm={6} md={4} key={fitnessItem.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6" component="h2">
                      {fitnessItem.fitnessName}
                    </Typography>
                    <Chip
                      label={`Level ${fitnessItem.difficultyLevel || 1}`}
                      size="small"
                      color={getDifficultyColor(fitnessItem.difficultyLevel)}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Code: {fitnessItem.fitnessCode}
                  </Typography>
                  <Chip label={fitnessItem.fitnessType} size="small" sx={{ mt: 1, mr: 1 }} />
                  {fitnessItem.metricName && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Metric:</strong> {fitnessItem.metricName}
                        {fitnessItem.metricUnit && ` (${fitnessItem.metricUnit})`}
                      </Typography>
                    </Box>
                  )}
                  {fitnessItem.description && (
                    <Typography variant="body2" sx={{ mt: 2 }} noWrap>
                      {fitnessItem.description}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<PlayIcon />}
                    onClick={() => handleViewDetail(fitnessItem)}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Fitness Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedFitness?.fitnessName}</DialogTitle>
        <DialogContent>
          {selectedFitness && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Code: {selectedFitness.fitnessCode}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Chip label={selectedFitness.fitnessType} size="small" />
                <Chip
                  label={`Difficulty: ${selectedFitness.difficultyLevel || 1}`}
                  size="small"
                  color={getDifficultyColor(selectedFitness.difficultyLevel)}
                />
              </Box>
              {selectedFitness.metricName && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Metric</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedFitness.metricName}
                    {selectedFitness.metricUnit && ` (${selectedFitness.metricUnit})`}
                  </Typography>
                </>
              )}
              {selectedFitness.description && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Description</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{selectedFitness.description}</Typography>
                </>
              )}
              {selectedFitness.instructions && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Instructions</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{selectedFitness.instructions}</Typography>
                </>
              )}
              {selectedFitness.referenceVideos && selectedFitness.referenceVideos.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Reference Videos</Typography>
                  <Box sx={{ mt: 1 }}>
                    {selectedFitness.referenceVideos.map((url, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        href={url}
                        target="_blank"
                        sx={{ mr: 1, mb: 1 }}
                      >
                        Video {index + 1}
                      </Button>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => {
            // TODO: Link to record performance when implemented
            setDetailDialogOpen(false);
          }}>
            Record Performance
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlayerFitnessPage;

